/**
 * Switching workspaces saves the one being left without making the switch wait for it.
 * Its save is kept here until done, so anything that reads that workspace back (opening it
 * again, the library's list and thumbnails) can wait for it first.
 */

export interface PendingSaves {
    /** Keeps a save of `id` until it is done; `onError` hears of a failure. */
    track: (id: string, save: Promise<unknown>, onError?: (error: unknown) => void) => void;
    /** Resolves when `id` has no save running (at once when it never had one). */
    waitFor: (id: string) => Promise<void>;
    /** Resolves when every save kept here is done. */
    waitForAll: () => Promise<void>;
    isSaving: (id: string) => boolean;
    /** Whether any save kept here is still running. */
    isSavingAny: () => boolean;
}

export const createPendingSaves = (): PendingSaves => {
    const running = new Map<string, Promise<void>>();
    return {
        track: (id, save, onError) => {
            // A newer save of the same workspace runs after the one before it
            const before = running.get(id) || Promise.resolve();
            const done: Promise<void> = before
                .then(() => save)
                .then(() => undefined, (error) => { onError?.(error); })
                .finally(() => { if (running.get(id) === done) running.delete(id); });
            running.set(id, done);
        },
        waitFor: (id) => running.get(id) || Promise.resolve(),
        waitForAll: () => Promise.all(Array.from(running.values())).then(() => undefined),
        isSaving: (id) => running.has(id),
        isSavingAny: () => running.size > 0,
    };
};

/**
 * An auto-save runs two seconds after the last change, so for those two seconds the change is
 * saved nowhere: a window closed or reloaded then takes it along. This keeps the saves that
 * are waiting and the ones under way, so that closing the window can run the waiting ones at
 * once and wait for them all (`flush`).
 */
export interface AutoSaves {
    /**
     * Runs `save` after `delayMs`, unless it is taken back first (the returned function) or
     * run earlier by `flush`. One save waits under each `key`: a new one takes the place of the
     * one before.
     */
    later: (key: string, save: () => Promise<unknown>, delayMs: number) => () => void;
    /**
     * Takes back the waiting saves (the one under `key`, when given): what they would have
     * saved is covered otherwise, or planned again.
     */
    cancelWaiting: (key?: string) => void;
    /** Counts `work` among the saves under way until it is done, and returns it. */
    whileSaving: <T>(work: Promise<T>) => Promise<T>;
    /** Runs the waiting saves now, and resolves when no save is under way any more. Never rejects. */
    flush: () => Promise<void>;
    /** Whether a save is waiting or under way. */
    busy: () => boolean;
}

export const createAutoSaves = (): AutoSaves => {
    interface Waiting { timer: ReturnType<typeof setTimeout>; start: () => void }
    const waiting = new Map<string, Waiting>();
    const running = new Set<Promise<void>>();
    const whileSaving = <T>(work: Promise<T>): Promise<T> => {
        const done: Promise<void> = work.then(() => undefined, () => undefined).finally(() => { running.delete(done); });
        running.add(done);
        return work;
    };
    const takeBack = (key: string, entry: Waiting | undefined) => {
        if (!entry || waiting.get(key) !== entry) return;
        clearTimeout(entry.timer);
        waiting.delete(key);
    };
    return {
        later: (key, save, delayMs) => {
            takeBack(key, waiting.get(key));
            const entry: Waiting = {
                timer: setTimeout(() => entry.start(), delayMs),
                start: () => {
                    takeBack(key, entry);
                    // (a save that throws before its first wait has failed like any other)
                    let work: Promise<unknown>;
                    try { work = Promise.resolve(save()); } catch (error) { work = Promise.reject(error); }
                    whileSaving(work).catch(() => undefined);
                },
            };
            waiting.set(key, entry);
            return () => takeBack(key, entry);
        },
        cancelWaiting: (key) => {
            for (const [name, entry] of Array.from(waiting)) {
                if (key === undefined || key === name) takeBack(name, entry);
            }
        },
        whileSaving,
        flush: async () => {
            for (const entry of Array.from(waiting.values())) entry.start();
            // (until none is left: a save may have started while these were waited for)
            while (running.size > 0) await Promise.all(Array.from(running));
        },
        busy: () => waiting.size > 0 || running.size > 0,
    };
};

/** A save the cloud did not take, though it was tried (the copy on this device was still written). */
export class CloudSaveFailed extends Error {
    constructor() {
        super('The cloud did not take the save');
        this.name = 'CloudSaveFailed';
    }
}

/**
 * saveProject reports a cloud save that did not get through in its result, without throwing.
 * For a save on leaving a workspace that is a failure like any other: this throws for it, so
 * whoever waits for the save, or tracks it above, hears of it. Any other result is passed on.
 */
export const failIfCloudFailed = <T extends { cloud: 'saved' | 'failed' | 'off' }>(result: T): T => {
    if (result.cloud === 'failed') throw new CloudSaveFailed();
    return result;
};

/** The work's result, or undefined when it takes longer than `ms` or fails. The work itself carries on. */
export const withinTime = <T>(work: Promise<T>, ms: number): Promise<T | undefined> => new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), ms);
    work.then(
        (value) => { clearTimeout(timer); resolve(value); },
        () => { clearTimeout(timer); resolve(undefined); },
    );
});

/** Resolves once what was just rendered is on screen, or after `fallbackMs` when frames are paused (a background tab). */
export const afterPaint = (fallbackMs = 100): Promise<void> => new Promise((resolve) => {
    let settled = false;
    const finish = () => { if (!settled) { settled = true; resolve(); } };
    const timer = setTimeout(finish, fallbackMs);
    requestAnimationFrame(() => setTimeout(() => { clearTimeout(timer); finish(); }, 0));
});

/**
 * Runs `work` once `close` has taken something off the screen: a dialog whose row started the
 * work, when the work begins with a picture of what the window shows (in the desktop app, the
 * thumbnail of the workspace being left). The dialog is closed first and a frame is let
 * through (`painted`), so the picture is of the canvas and not of the dialog over it.
 */
export const onceOffScreen = async <T>(close: () => void, work: () => Promise<T> | T, painted: () => Promise<void> = afterPaint): Promise<T> => {
    close();
    await painted();
    return work();
};
